/**
 * Les sous-onglets de l'onglet « Entraînements » d'un enfant : les cahiers, les
 * entraînements composés, les sujets entiers, à valider.
 *
 * Hedi, le 26 septembre 2026 : *« Est ce que tu as implémenté la gestion des
 * entrainements ? ouvert, clos, en cours, pdf … je ne le vois pas »*. Tout
 * existait, était routé, servi et déployé — et la seule façon d'y arriver était
 * un lien discret au milieu de l'écran des cahiers, qui **disparaissait dès
 * qu'on ouvrait un cahier**, c'est-à-dire exactement au moment où l'on vient de
 * composer. La demande disait « un sous tab appelé entraînements » ; c'était un
 * lien, et sur un seul écran.
 *
 * **Le défaut n'était pas dans le dessin, il était dans le routage** — le même
 * qu'au 6 septembre côté `/parents`, corrigé par `ChildLayout` : les écrans
 * d'un enfant étaient des routes *sœurs*, si bien qu'en ouvrir un remplaçait la
 * page entière, y compris ce qui menait aux autres. Cette route-ci est un
 * layout : elle porte la rangée et un `<Outlet/>`, donc la navigation d'un
 * enfant survit à la navigation *dans* cet enfant — y compris deux niveaux plus
 * bas, dans un chapitre.
 *
 * Depuis le 27 septembre 2026, elle est montée **dans la fiche de l'enfant**
 * (`/parents/enfant/:id/entrainements`), sous l'onglet « Entraînements », et
 * non plus dans une coquille à part : l'enfant est choisi par la barre du haut,
 * et il n'y a plus de prénoms à choisir ici. L'ancien sous-onglet
 * « Entraînements » s'appelle « Composés », le nom de l'onglet qui le contient
 * étant pris.
 *
 * `SousOnglets` vit dans `ui/`, `v2/` n'ayant pas le droit d'importer
 * `parent/`. Le recopier aurait donné deux rangées qui finissent par ne plus se
 * comporter pareil.
 */

import { Outlet, useParams } from "react-router-dom";

import type { ChildSummary } from "@/api/schema";
import { useEnfants } from "@/ui/enfants";
import { SousOnglets, type SousOnglet } from "@/ui/SousOnglets";
import { entrainementsDe } from "@/v2/chemins";

/**
 * Les sous-onglets, pour un enfant donné.
 *
 * « Carnet de la semaine » **n'y est que si le carnet est allumé pour cet
 * enfant** (`carnet_actif`, lu par le serveur sur `TEACHER_CARNET_PAPIER`). Éteint,
 * toutes les routes du carnet répondent 404 : un onglet qui s'ouvre sur une
 * erreur est l'apparence exacte d'une panne. Tant que la liste des enfants n'est
 * pas chargée, on ne sait pas — et l'onglet attend plutôt que de mener à un 404.
 */
export function ongletsDesEntrainements(
  childId: string,
  enfant: Pick<ChildSummary, "carnet_actif"> | undefined,
): SousOnglet[] {
  const base = entrainementsDe(childId);

  // « Les cahiers » est `end`, et c'est le moindre des deux défauts : les deux
  // niveaux du cahier (`/:ouvrage`, `/:ouvrage/:cle`) sont sous cette adresse,
  // mais « composes » et « a-valider » aussi. Sans `end`, trois onglets
  // s'allumeraient ensemble sur l'écran des entraînements composés — une rangée
  // qui dit qu'on est à trois endroits à la fois est pire qu'une rangée éteinte
  // pendant qu'on feuillette, où le titre de l'écran dit déjà où l'on est.
  return [
    { to: base, label: "Les cahiers", end: true },
    { to: `${base}/generer`, label: "Générer" },
    { to: `${base}/composes`, label: "Composés" },
    // Un sujet des annales, donné en entier (Hedi, 26 septembre 2026). À côté
    // des entraînements composés et non dedans : il ne se compose pas exercice
    // par exercice, il se donne tel quel et se juge en entier.
    { to: `${base}/sujets`, label: "Sujets entiers" },
    // Une fiche par soir de classe, imprimée le dimanche (P1, septembre 2026).
    ...(enfant?.carnet_actif === true
      ? [{ to: `${base}/carnet`, label: "Carnet de la semaine" }]
      : []),
    { to: `${base}/a-valider`, label: "À valider" },
  ];
}

export function V2ChildLayout() {
  const { childId = "" } = useParams();
  const enfant = useEnfants().find((candidat) => candidat.child_id === childId);
  const tabs = ongletsDesEntrainements(childId, enfant);

  return (
    <>
      <SousOnglets
        tabs={tabs}
        label={enfant ? `Entraînements de ${enfant.display_name}` : "Entraînements"}
      />
      <Outlet />
    </>
  );
}
